/**
 * As integrações de dados (fase 1 das listas, #922): as conexões aos bancos
 * dos clientes, o mapa do banco e as operações SQL.
 *
 * Moram no serviço de automações, que é a fonte da verdade (ADR-0021); o CRM
 * não guarda cópia e estas rotas são o vocabulário do CRM sobre elas. Só os
 * devs da Cliqfy (`operacao_cliqfy`, revisão de 19/10) as usam.
 *
 * A senha da conexão é só de escrita: nenhuma resposta a devolve.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';

export const FamiliaDeBancoSchema = z.enum(['postgres', 'mysql', 'sqlserver']);

export const ConexaoSchema = z
  .object({
    id: IdSchema,
    nome: z.string(),
    familia: FamiliaDeBancoSchema,
    host: z.string(),
    porta: z.number().int(),
    banco: z.string(),
    usuario: z.string(),
    /** `direto`: o serviço alcança o banco; `gateway`: por um agente na rede do cliente. */
    cenario: z.enum(['direto', 'gateway']),
    situacao: z.enum(['nao_testada', 'ok', 'falhou']),
    testada_em: z.string().nullable(),
  })
  .strict();

export type ConexaoDaApi = z.infer<typeof ConexaoSchema>;

export const ConexoesSchema = z.object({ itens: z.array(ConexaoSchema) }).strict();

const CamposDaConexao = {
  nome: z.string().trim().min(1).max(120),
  host: z.string().trim().min(1).max(255),
  porta: z.number().int().min(1).max(65535),
  banco: z.string().trim().min(1).max(128),
  usuario: z.string().trim().min(1).max(128),
  cenario: z.enum(['direto', 'gateway']),
};

export const CriarConexaoSchema = z
  .object({
    ...CamposDaConexao,
    familia: FamiliaDeBancoSchema,
    senha: z.string().min(1).max(512),
  })
  .strict();

export type CriarConexao = z.infer<typeof CriarConexaoSchema>;

/** Editar troca tudo, menos a família. Sem `senha`, a atual fica. */
export const EditarConexaoSchema = z
  .object({ ...CamposDaConexao, senha: z.string().min(1).max(512).optional() })
  .strict();

export type EditarConexao = z.infer<typeof EditarConexaoSchema>;

/** O teste da conexão: uma falha é resposta (200 com `ok: false`), não erro. */
export const TesteDeConexaoSchema = z
  .object({
    ok: z.boolean(),
    etapas: z.array(
      z.object({ nome: z.string(), ok: z.boolean(), detalhe: z.string().nullable() }).strict(),
    ),
  })
  .strict();

export const MapaDoBancoSchema = z
  .object({
    versao: z.number().int(),
    /** Só na atualização: o mapa mudou desde a última leitura. */
    mudou: z.boolean().optional(),
    tabelas: z.array(
      z
        .object({
          esquema: z.string(),
          nome: z.string(),
          colunas: z.array(
            z
              .object({
                nome: z.string(),
                tipo: z.string(),
                aceita_vazio: z.boolean(),
                tamanho_maximo: z.number().nullable(),
              })
              .strict(),
          ),
        })
        .strict(),
    ),
  })
  .strict();

export const FinalidadeDaOperacaoSchema = z.enum(['consulta', 'listagem', 'acao']);

export const ColunaDeSaidaSchema = z
  .object({
    nome: z.string().min(1).max(128),
    tipo: z.enum(['texto', 'numero', 'booleano', 'data']),
    aceita_vazio: z.boolean().optional(),
    opcional: z.boolean().optional(),
  })
  .strict();

export const ParametroDaOperacaoSchema = z
  .object({
    nome: z.string().regex(/^[a-z_][a-z0-9_]{0,63}$/),
    tipo: z.string().min(1).max(32),
  })
  .strict();

export const OperacaoSchema = z
  .object({
    id: IdSchema,
    conexao_id: IdSchema,
    nome: z.string(),
    sql: z.string(),
    /** Detectado do SQL: leitura nasce publicada; escrita espera aprovação. */
    tipo: z.enum(['leitura', 'escrita']),
    finalidade: FinalidadeDaOperacaoSchema,
    tabelas: z.array(z.string()),
    /** Na ordem em que aparecem no SQL. */
    parametros: z.array(ParametroDaOperacaoSchema),
    colunas: z.array(ColunaDeSaidaSchema).nullable(),
    limites: z
      .object({ maximo_de_linhas: z.number().int(), tempo_maximo_ms: z.number().int() })
      .strict(),
    versao: z.number().int(),
    situacao: z.enum(['rascunho', 'publicada', 'recusada']),
    autor_id: z.string(),
    aprovador_id: z.string().nullable(),
  })
  .strict();

export type OperacaoDaApi = z.infer<typeof OperacaoSchema>;

export const OperacoesSchema = z.object({ itens: z.array(OperacaoSchema) }).strict();

export const SalvarOperacaoSchema = z
  .object({
    conexao_id: IdSchema,
    nome: z.string().trim().min(1).max(120),
    sql: z.string().min(1).max(20_000),
    finalidade: FinalidadeDaOperacaoSchema,
    parametros: z.array(ParametroDaOperacaoSchema).max(40).default([]),
    colunas: z.array(ColunaDeSaidaSchema).min(1).max(100).nullable().default(null),
    limites: z
      .object({
        maximo_de_linhas: z.number().int().min(1).max(10_000),
        tempo_maximo_ms: z.number().int().min(100).max(30_000),
      })
      .strict(),
  })
  .strict();

export type SalvarOperacao = z.infer<typeof SalvarOperacaoSchema>;

/** Os valores dos parâmetros, por nome. O que a operação não declara é recusado. */
export const ExecutarOperacaoSchema = z
  .object({
    parametros: z
      .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
      .default({}),
  })
  .strict();

export type ExecutarOperacao = z.infer<typeof ExecutarOperacaoSchema>;

/** O resultado de um teste ou de uma execução. O CRM não guarda as linhas. */
export const ResultadoDaExecucaoSchema = z
  .object({
    tipo: z.enum(['leitura', 'escrita']),
    linhas: z.array(z.record(z.string(), z.unknown())),
    /** Na leitura, o total antes do corte; na escrita, as linhas afetadas. */
    total: z.number().int(),
    cortada: z.boolean(),
  })
  .strict();

export const HistoricoDaOperacaoSchema = z
  .object({
    itens: z.array(
      z
        .object({
          versao: z.number().int(),
          acao: z.string(),
          autor_id: z.string(),
          em: z.string(),
          resumo: z.string().nullable(),
        })
        .strict(),
    ),
  })
  .strict();
